import { Box, Button, IconButton, Paper, Stack, TextField, Typography } from '@mui/material';
import { configButtonStylesContained, configButtonStylesOutlined } from '@old/styles/generic-styles';
import ChevronLeftIcon from '@old/assets/svg/core/chevron-left-blue.svg';
import { useNavigate } from 'react-router';
import { ChangeEvent, Dispatch, DragEvent, useRef, useState } from 'react';
import { useService } from '@old/hooks/use-service';
import { warrantOrganizationCreationService } from '@old/services/domain/base/administrator/warrants/warrants-create-organization';
import { useGlobalStore } from '@old/global-store/use-store';
import { useEffectOnUpdate, useToggle } from '@old/hooks/core';
import UploadIcon from '@old/assets/svg/core/upload.svg';
import DeleteIcon from '@old/assets/svg/core/trash-red.svg';
import DeleteDisableIcon from '@old/assets/svg/core/trash-disabled.svg';

import UploadIconDisabled from '@old/assets/svg/core/upload-disabled.svg';
import i18n from '../../libs/language';
import { PATH } from '@old/data/core/paths';
// import { warrantUploadFilesService } from '@old/services/domain/base/administrator/warrants/warrants-files-upload';
import { ENDPOINT } from '@old/data/core/endpoints';
import { transformIntoQueryParamString } from '@old/utils/service';
import { warrantLegalRepresentativeCreationService } from '@old/services/domain/base/administrator/warrants/warrants-legal-representative';
import { ToastAction } from '@old/ui/toast/toast-action';
import { getWarrantReferenceService } from '@old/services/domain/base/administrator/warrants/get-warrant-reference';

type InputChg = ChangeEvent<HTMLInputElement>;

type ApoderadosData = {
  companyId: string;
  shortName: string;
  attributes: Record<string, string>;
};

export function WarrantsPage() {
  const { userOrganizationId, accessToken } = useGlobalStore();
  const { summary: warrantsReferences, isSuccess: successReferences } = useService(
    getWarrantReferenceService,
    {
      fetchOnMount: [{ query: { externalReference: userOrganizationId } }],
    }
  );

  const navigate = useNavigate();
  const hasWarrantResults = warrantsReferences && warrantsReferences.response.results.length > 0;
  const [step, setStep] = useState<'INFO' | 'FILES'>('INFO');

  const [createOrganization, setCreateOrganization] = useState({
    name: hasWarrantResults ? warrantsReferences?.response.results[0].name : '',
    taxNumber: hasWarrantResults ? warrantsReferences?.response.results[0].taxNumber : '',
    home: hasWarrantResults ? warrantsReferences?.response.results[0].attributes.home : '',
    commercialTelephone: hasWarrantResults
      ? warrantsReferences?.response.results[0].attributes.commercialTelephone
      : '',
    emailContact: hasWarrantResults ? warrantsReferences?.response.results[0].attributes.emailContact : '',
  });
  //EN CASO DE TENER LA COMPANIA CARGADA, SE CARGAN LOS VALORES DE LA MISMA
  useEffectOnUpdate(() => {
    if (warrantsReferences && successReferences && warrantsReferences?.response.results[0]) {
      setCreateOrganization({
        name: hasWarrantResults ? warrantsReferences?.response.results[0].name : '',
        taxNumber: hasWarrantResults ? warrantsReferences?.response.results[0].taxNumber : '',
        home: hasWarrantResults ? warrantsReferences?.response.results[0].attributes.home : '',
        commercialTelephone: hasWarrantResults
          ? warrantsReferences?.response.results[0].attributes.commercialTelephone
          : '',
        emailContact: hasWarrantResults
          ? warrantsReferences?.response.results[0].attributes.emailContact
          : '',
      });
      setCompanyId(hasWarrantResults ? warrantsReferences?.response.results[0].companyId : '');
      const hasApoderados = warrantsReferences?.response.results[0].legalRepresentatives.length > 0;
      const hasFiles = warrantsReferences?.response.results[0].files.length > 0;
      if (hasApoderados) {
        setApoderados(
          warrantsReferences?.response.results[0].legalRepresentatives.map((apod) => ({
            companyId: apod.companyId,
            shortName: apod.shortName,
            attributes: {
              dni: apod.attributes.dni,
              home: apod.attributes.home,
            },
          }))
        );
      }
      if (hasFiles) {
        const filesFromResponse = warrantsReferences?.response.results[0].files;

        if (filesFromResponse) {
          const afipFile = filesFromResponse.find(
            (file) => file.fileType.name === 'CONSTANCIA INSCRIPCIÓN AFIP'
          );
          if (afipFile) {
            setAfipFileName(new File([afipFile.s3Key], afipFile.originalName, { type: afipFile.mimeType }));
          }
          const updatedFiles = {
            LEGAL_REPRESENTATIVE_AUTHORIZATION: filesFromResponse
              .filter((file) => file.fileType.code === 'LEGAL_REPRESENTATIVE_AUTHORIZATION')
              .map((file) => new File([file.s3Key], file.originalName, { type: file.mimeType })),
            COMPANY_STATUTE: filesFromResponse
              .filter((file) => file.fileType.code === 'COMPANY_STATUTE')
              .map((file) => new File([file.s3Key], file.originalName, { type: file.mimeType })),
            COMPANY_BALANCE_SHEET: filesFromResponse
              .filter((file) => file.fileType.code === 'COMPANY_BALANCE_SHEET')
              .map((file) => new File([file.s3Key], file.originalName, { type: file.mimeType })),
          };

          setFiles(updatedFiles);
        }
      }
      setDisableApoderadoData(hasApoderados);
      setDisableFleData(hasFiles);
      setStep('FILES');
    }
  }, [warrantsReferences, successReferences]);

  const initErrorData = {
    name: '',
    taxNumber: '',
    home: '',
    commercialTelephone: '',
    emailContact: '',
  };

  const initErrorApoderado = {
    shortName: '',
    dni: '',
    home: '',
  };

  const [disableOrganizationForm, setDisableOrganizationForm] = useToggle(false);
  const [disableApoderadoData, setDisableApoderadoData] = useToggle(true);
  const [disableFileData, setDisableFleData] = useToggle(true);

  const [afipFileName, setAfipFileName] = useState<File | null>(null);

  const [errorData, setErrorData] = useState(initErrorData);
  const [errorApoderados, setErrorApoderados] =
    useState<Record<number, typeof initErrorApoderado>>(initErrorApoderado);

  const [companyId, setCompanyId] = useState('');

  type ToastData = {
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
    open: boolean;
  };

  const [companyCreationToast, setCompanyCreationToast] = useState<ToastData>({
    message: '',
    severity: 'success',
    open: false,
  });

  const [apoderadoToast, setApoderadoToast] = useState<ToastData>({
    message: '',
    severity: 'success',
    open: false,
  });

  const [filesToast, setFilesToast] = useState<ToastData>({
    message: '',
    severity: 'success',
    open: false,
  });

  const closeCompanyToast = () => setCompanyCreationToast((toast) => ({ ...toast, open: false }));
  const closeApoderadoToast = () => setApoderadoToast((toast) => ({ ...toast, open: false }));
  const closeFilesToast = () => setFilesToast((toast) => ({ ...toast, open: false }));

  const setErrorForApoderado = (index: number, field: keyof typeof initErrorApoderado, errorMsg: string) => {
    setErrorApoderados((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [field]: errorMsg,
      },
    }));
  };

  const [files, setFiles] = useState<{
    LEGAL_REPRESENTATIVE_AUTHORIZATION: File[];
    COMPANY_STATUTE: File[];
    COMPANY_BALANCE_SHEET: File[];
  }>({
    LEGAL_REPRESENTATIVE_AUTHORIZATION: [],
    COMPANY_STATUTE: [],
    COMPANY_BALANCE_SHEET: [],
  });

  const [apoderados, setApoderados] = useState<ApoderadosData[]>([
    {
      companyId: hasWarrantResults ? warrantsReferences?.response.results[0].companyId : '',
      shortName: '',
      attributes: {
        dni: '',
        home: '',
      },
    },
  ]);

  const onCreationWarrantSuccess = () => {
    setDisableOrganizationForm(true);
    setDisableApoderadoData(false);
    setDisableFleData(false);
    setStep('FILES');
    setCompanyCreationToast({ message: 'Se creó la compañía con éxito', severity: 'success', open: true });
  };

  const onCreationWarrantError = () => {
    setCompanyCreationToast({
      message: 'Hubo un problema al crear la empresa',
      severity: 'error',
      open: true,
    });
  };

  const {
    trigger: triggerCreateWarrantOrganization,
    summary: summaryWarrantOrganization,
    isLoading: loadingWarrantOrganization,
  } = useService(warrantOrganizationCreationService, {
    onSuccess: onCreationWarrantSuccess,
    onError: onCreationWarrantError,
  });

  const onSuccessCreateWarrantLegalRepresentative = () => {
    setApoderadoToast({
      message: 'Se crearon los apoderados correctamente.',
      severity: 'success',
      open: true,
    });
    setDisableApoderadoData(true);
  };

  const onErrorCreateWarrantLegalRepresentative = () => {
    setApoderadoToast({
      message: 'Hubo un error al crear los apoderados.',
      severity: 'error',
      open: true,
    });
    setDisableApoderadoData(false);
  };

  const { trigger: triggerCreateWarrantLegalRepresentative, isLoading: loadingWarrantLegalRepresentative } =
    useService(warrantLegalRepresentativeCreationService, {
      onSuccess: onSuccessCreateWarrantLegalRepresentative,
      onError: onErrorCreateWarrantLegalRepresentative,
    });

  useEffectOnUpdate(() => {
    const newCompanyId = summaryWarrantOrganization?.response.companyId || '';
    setCompanyId(newCompanyId);
    // Actualizar el companyId del primer apoderado si existe
    setApoderados((prev) =>
      prev.map((apoderado, index) => (index === 0 ? { ...apoderado, companyId: newCompanyId } : apoderado))
    );
  }, [summaryWarrantOrganization]);
  // const { trigger: triggerUploadFiles } = useService(warrantUploadFilesService);
  const rootDataError = (key: string, value: string) => setErrorData((prev) => ({ ...prev, [key]: value }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCreateOrganization((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const uploadAfipFilRef = useRef<HTMLInputElement>(null);
  const handleOpenFileExplorer = () => uploadAfipFilRef.current?.click();

  const handleAfipFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    const hasFiles = files && files.length > 0;
    if (!hasFiles) return;

    const [file] = Array.from(files);

    if (file) {
      setAfipFileName(file);
    }
  };

  const VALIDATION = {
    ALPHANUMERIC_TEXT: (value: string) => !/^[\w0-9\s-_&.áàâãéêíóôõúñç]*$/i.test(value),
    CUIT_TEXT: (value: string) => !/^[0-9-]*$/.test(value),
    IS_EMAIL: (value: string) =>
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value
      ),
    ONLY_NUMS: (value: string) => !/^[0-9]*$/.test(value),
  };

  const ORGANIZATION_HANDLERS = {
    name: (event: InputChg) => {
      const input = event.target.value;
      if (VALIDATION.ALPHANUMERIC_TEXT(input)) {
        const errorMsg =
          'Solo se aceptan caracteres alfanuméricos, espacios, guiones (-), guiones bajos (_) y ampersand (&)';
        rootDataError('name', errorMsg);
        return;
      }
      rootDataError('name', '');
      handleChange(event);
    },
    taxNumber: (event: InputChg) => {
      const input = event.target.value;
      if (VALIDATION.CUIT_TEXT(input)) {
        const errorMsg = 'Solo se aceptan caracteres alfanuméricos y guiones (-),';
        rootDataError('taxNumber', errorMsg);
        return;
      }
      rootDataError('taxNumber', '');
      handleChange(event);
    },
    home: (event: InputChg) => {
      const input = event.target.value;
      if (VALIDATION.ALPHANUMERIC_TEXT(input)) {
        const errorMsg =
          'Solo se aceptan caracteres alfanuméricos, espacios, guiones (-), guiones bajos (_) y ampersand (&)';
        rootDataError('home', errorMsg);
        return;
      }
      rootDataError('home', '');
      handleChange(event);
    },
    emailContact: (event: InputChg) => {
      rootDataError('emailContact', '');
      handleChange(event);
      if (!VALIDATION.IS_EMAIL(event.target.value)) {
        rootDataError('emailContact', i18n.t('app.company_modal.email_format'));
      }
    },
    commercialTelephone: (event: InputChg) => {
      const input = event.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        const errorMsg = 'Por favor ingrese un número de teléfono válido.';
        rootDataError('commercialTelephone', errorMsg);
        return;
      }
      rootDataError('commercialTelephone', '');
      handleChange(event);
    },
  };

  const APODERADOS_HANDLERS = {
    shortName: (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value;
      if (VALIDATION.ALPHANUMERIC_TEXT(input)) {
        const errorMsg =
          'Solo se aceptan caracteres alfanuméricos, espacios, guiones (-), guiones bajos (_) y ampersand (&)';
        setErrorForApoderado(index, 'shortName', errorMsg);
        return;
      }
      setErrorForApoderado(index, 'shortName', '');
      handleChangeApoderado(index, 'shortName', input);
    },
    dni: (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value;
      if (VALIDATION.ONLY_NUMS(input)) {
        const errorMsg = 'El DNI solo debe contener números.';
        setErrorForApoderado(index, 'dni', errorMsg);
        return;
      }

      setErrorForApoderado(index, 'dni', '');
      handleChangeApoderado(index, 'dni', input);
    },
    home: (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value;
      if (VALIDATION.ALPHANUMERIC_TEXT(input)) {
        const errorMsg =
          'Solo se aceptan caracteres alfanuméricos, espacios, guiones (-), guiones bajos (_) y ampersand (&)';
        setErrorForApoderado(index, 'home', errorMsg);
        return;
      }
      setErrorForApoderado(index, 'home', '');
      handleChangeApoderado(index, 'home', input);
    },
  };

  const handleSaveCompany = () => {
    const errorMsg = i18n.t('app.must_field');
    let error = false;
    if (createOrganization.name === '') (error = true), rootDataError('name', errorMsg);
    if (createOrganization.taxNumber === '') (error = true), rootDataError('taxNumber', errorMsg);
    if (createOrganization.home === '') (error = true), rootDataError('home', errorMsg);
    if (createOrganization.commercialTelephone === '')
      (error = true), rootDataError('commercialTelephone', errorMsg);
    if (createOrganization.emailContact === '') (error = true), rootDataError('emailContact', errorMsg);

    if (error) return;

    const body = {
      externalReference: userOrganizationId?.toString() ?? '',
      name: createOrganization.name,
      taxNumber: createOrganization.taxNumber,
      attributes: {
        home: createOrganization.home,
        commercialTelephone: createOrganization.commercialTelephone,
        emailContact: createOrganization.emailContact,
      },
      active: true,
    };

    triggerCreateWarrantOrganization({ body: body });
  };

  const handleUploadFiles = () => {
    if (disableFileData) return;
    const AFIP_FILE = 'TAX_ID_DOCUMENT';
    const APODERADO_FILE = 'LEGAL_REPRESENTATIVE_AUTHORIZATION';
    const SOCIETARIO_FILE = 'COMPANY_STATUTE';
    const BALANCE_FILE = 'COMPANY_BALANCE_SHEET';

    // TODO: Hablar con Emilio cuando vuelva de vacaciones para que permita enviar array de documentos
    // triggerUploadFiles({
    //   query: { fileTypeCode: AFIP_FILE, companyId: companyId },
    //   body: { files: afipFileName ? [afipFileName] : [] },
    // });

    const fileGroups = [
      { fileTypeCode: AFIP_FILE, files: [afipFileName as File] },
      { fileTypeCode: APODERADO_FILE, files: files.LEGAL_REPRESENTATIVE_AUTHORIZATION },
      { fileTypeCode: SOCIETARIO_FILE, files: files.COMPANY_STATUTE },
      { fileTypeCode: BALANCE_FILE, files: files.COMPANY_BALANCE_SHEET },
    ];

    const hasMissingFiles = fileGroups.some(({ files }) => files.length === 0);

    if (hasMissingFiles) {
      setFilesToast({
        message: 'Todos los tipos de archivos deben tener al menos un documento.',
        severity: 'error',
        open: true,
      });
      return;
    }

    setDisableFleData(true);
    fileGroups.forEach(({ fileTypeCode, files: fileList }) => {
      const query = transformIntoQueryParamString({ fileTypeCode, companyId });
      const formData = new FormData();
      fileList.forEach((file: File) => formData.append('files', file));

      fetch(`${import.meta.env.VITE_IOF_API}${ENDPOINT.WARRANT_FILES_UPLOAD}${query}`, {
        method: 'POST',
        body: formData,
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    });
  };

  const handleCreateLegalRepresentative = () => {
    let hasErrors = false;
    if (disableApoderadoData) return;
    apoderados.forEach(({ shortName, attributes }, index) => {
      if (!shortName.trim()) {
        setErrorForApoderado(index, 'shortName', 'El nombre del apoderado es obligatorio.');
        hasErrors = true;
      } else {
        setErrorForApoderado(index, 'shortName', ''); // Limpia el error si es válido
      }

      if (!attributes || Object.values(attributes).some((value) => !value.trim())) {
        setErrorForApoderado(index, 'dni', 'El DNI del apoderado es obligatorio');
        hasErrors = true;
      } else {
        setErrorForApoderado(index, 'dni', ''); // Limpia el error si es válido
      }

      if (!attributes || Object.values(attributes).some((value) => !value.trim())) {
        setErrorForApoderado(index, 'home', 'El domicilio del apoderado es obligatorio.');
        hasErrors = true;
      } else {
        setErrorForApoderado(index, 'home', ''); // Limpia el error si es válido
      }
    });

    if (hasErrors) return;

    const body = apoderados.map(({ companyId, shortName, attributes }) => ({
      companyId,
      shortName,
      attributes,
      active: true,
    }));
    triggerCreateWarrantLegalRepresentative({ body });
  };

  const handleSave = () => {
    if (step === 'INFO') {
      handleSaveCompany();
    } else if (step === 'FILES') {
      handleUploadFiles();
      handleCreateLegalRepresentative();
    }
  };

  const handleAddApoderado = () => {
    setApoderados((prev) => [
      ...prev,
      {
        companyId: companyId,
        shortName: '',
        attributes: {
          dni: '',
          home: '',
        },
      },
    ]);
  };

  const handleRemoveApoderado = (indexToRemove: number) => {
    setApoderados((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleChangeApoderado = (
    index: number,
    field: keyof ApoderadosData | keyof ApoderadosData['attributes'],
    value: string
  ) => {
    setApoderados((prev) =>
      prev.map((apod, i) =>
        i === index
          ? {
              ...apod,
              ...(field in apod
                ? { [field]: value }
                : { attributes: { ...apod.attributes, [field]: value } }),
            }
          : apod
      )
    );
  };

  const [finishWarrantCreation, setFinishWarrantCreation] = useToggle(false);
  useEffectOnUpdate(() => {
    if (disableFileData && disableApoderadoData && disableOrganizationForm) {
      setFinishWarrantCreation(true);

      setTimeout(() => {
        navigate(`${PATH.ADMINISTRATOR}`);
      }, 5000);
    }
  }, [disableFileData, disableApoderadoData, disableOrganizationForm]);

  return (
    <Box>
      <Stack spacing={8}>
        {apoderadoToast.open && (
          <ToastAction
            message={apoderadoToast.message}
            severity={apoderadoToast.severity}
            onClose={closeApoderadoToast}
          />
        )}
        {filesToast.open && (
          <ToastAction
            message={filesToast.message}
            severity={filesToast.severity}
            onClose={closeFilesToast}
          />
        )}
      </Stack>
      {companyCreationToast.open && (
        <ToastAction
          message={companyCreationToast.message}
          severity={companyCreationToast.severity}
          onClose={closeCompanyToast}
        />
      )}
      {finishWarrantCreation && (
        <ToastAction
          message={
            'Se creó el Warrant correctamente. Será redirigido a la pantalla de administración en 5 segundos.'
          }
          severity={'success'}
        />
      )}

      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Stack direction={'row'} alignItems={'center'}>
          <Button
            startIcon={<img src={ChevronLeftIcon} style={{ width: 10, height: 10 }} />}
            onClick={() => {
              navigate(PATH.ADMINISTRATOR);
            }}
          />

          <Typography variant="h5" fontWeight={'medium'}>
            {i18n.t('app.administrator.warrant_information')}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          size="medium"
          sx={configButtonStylesContained}
          onClick={handleSave}
          disabled={loadingWarrantOrganization || loadingWarrantLegalRepresentative}
        >
          Guardar
        </Button>
      </Stack>
      <Paper sx={{ backgroundColor: '#FFFFFF', padding: 2, borderRadius: 2, margin: 2 }}>
        {/* CREACION DE COMPAÑIA */}
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              variant="outlined"
              size="medium"
              label="Nombre completo de la sociedad"
              fullWidth
              value={createOrganization.name}
              onChange={ORGANIZATION_HANDLERS.name}
              name="name"
              helperText={errorData.name}
              error={Boolean(errorData.name)}
              required
              disabled={disableOrganizationForm || hasWarrantResults}
            />
            <TextField
              variant="outlined"
              size="medium"
              label="CUIT"
              name="taxNumber"
              fullWidth
              required
              value={createOrganization.taxNumber}
              helperText={errorData.taxNumber}
              error={Boolean(errorData.taxNumber)}
              disabled={disableOrganizationForm || hasWarrantResults}
              onChange={ORGANIZATION_HANDLERS.taxNumber}
            />
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              variant="outlined"
              size="medium"
              label="Domicilio"
              name="home"
              fullWidth
              value={createOrganization.home}
              helperText={errorData.home}
              error={Boolean(errorData.home)}
              disabled={disableOrganizationForm || hasWarrantResults}
              onChange={ORGANIZATION_HANDLERS.home}
              required
            />
            <TextField
              variant="outlined"
              size="medium"
              label="Teléfono Comercial"
              name="commercialTelephone"
              fullWidth
              value={createOrganization.commercialTelephone}
              helperText={errorData.commercialTelephone}
              error={Boolean(errorData.commercialTelephone)}
              onChange={ORGANIZATION_HANDLERS.commercialTelephone}
              disabled={disableOrganizationForm || hasWarrantResults}
              required
            />
            <TextField
              variant="outlined"
              size="medium"
              label="Email de contacto"
              name="emailContact"
              fullWidth
              value={createOrganization.emailContact}
              helperText={errorData.emailContact}
              error={Boolean(errorData.emailContact)}
              onChange={ORGANIZATION_HANDLERS.emailContact}
              disabled={disableOrganizationForm || hasWarrantResults}
              required
            />
          </Stack>
        </Stack>

        {/* APODERADOS */}
        <Box width={'100%'} marginTop={3} marginBottom={3}>
          <Stack>
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Typography variant="h6" fontWeight={'medium'}>
                Apoderados
              </Typography>
              <Button color="info" onClick={handleAddApoderado} disabled={disableApoderadoData}>
                + Apoderado
              </Button>
            </Box>
            {apoderados.map((apod, index) => {
              return (
                <Stack key={index} spacing={1} marginTop={1}>
                  <Stack direction={'row'} alignItems={'center'}>
                    <Typography>Apoderado {index + 1}</Typography>
                    <IconButton
                      aria-label="delete"
                      onClick={() => handleRemoveApoderado(index)}
                      disabled={disableApoderadoData}
                    >
                      <img src={disableApoderadoData ? DeleteDisableIcon : DeleteIcon} alt="delete-icon" />
                    </IconButton>
                  </Stack>

                  <Stack width={'100%'} direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      variant="outlined"
                      size="medium"
                      label="nombre y apellido"
                      name="nombre"
                      fullWidth
                      value={apod.shortName}
                      onChange={APODERADOS_HANDLERS.shortName(index)}
                      helperText={errorApoderados[index]?.shortName}
                      error={Boolean(errorApoderados[index]?.shortName)}
                      disabled={disableApoderadoData}
                      required
                    ></TextField>
                    <TextField
                      variant="outlined"
                      size="medium"
                      label="DNI"
                      name="dni"
                      fullWidth
                      value={apod.attributes.dni}
                      onChange={APODERADOS_HANDLERS.dni(index)}
                      helperText={errorApoderados[index]?.dni}
                      error={Boolean(errorApoderados[index]?.dni)}
                      disabled={disableApoderadoData}
                      required
                    ></TextField>
                    <TextField
                      variant="outlined"
                      size="medium"
                      label="Domicilio"
                      name="home"
                      fullWidth
                      value={apod.attributes.home}
                      onChange={APODERADOS_HANDLERS.home(index)}
                      helperText={errorApoderados[index]?.home}
                      error={Boolean(errorApoderados[index]?.home)}
                      disabled={disableApoderadoData}
                      required
                    ></TextField>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Box>

        {/* CONSTANCIA DE INSCRIPCION AFIP */}
        <Box width={'100%'} display={'flex'} justifyContent={'start'} alignItems={'center'} marginTop={2}>
          <Stack>
            <Typography>Constancia de inscripcion de AFIP *</Typography>
            <Stack direction={'row'} display={'flex'} justifyContent={'start'} alignItems={'center'}>
              <Button
                variant="outlined"
                size="medium"
                sx={{
                  ...configButtonStylesOutlined,
                  fontSize: { xs: 12.7, sm: 12.7 },
                }}
                disabled={disableFileData}
                startIcon={<img src={disableFileData ? UploadIconDisabled : UploadIcon} alt="download" />}
                onClick={handleOpenFileExplorer}
              >
                Elegir archivo
              </Button>
              <Typography variant="body2">
                {afipFileName ? afipFileName.name : 'Sin archivo seleccionado'}
              </Typography>
            </Stack>
            <input
              ref={uploadAfipFilRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleAfipFileChange}
            />
          </Stack>
        </Box>

        {/* ARCHIVOS ADICIONALES */}
        <FileUploadSection setFiles={setFiles} files={files} disable={disableFileData} />
      </Paper>
    </Box>
  );
}

type FileUploadType = {
  setFiles: Dispatch<
    React.SetStateAction<{
      LEGAL_REPRESENTATIVE_AUTHORIZATION: File[];
      COMPANY_STATUTE: File[];
      COMPANY_BALANCE_SHEET: File[];
    }>
  >;
  files: {
    LEGAL_REPRESENTATIVE_AUTHORIZATION: File[];
    COMPANY_STATUTE: File[];
    COMPANY_BALANCE_SHEET: File[];
  };
  disable: boolean;
};

const FileUploadSection = ({ setFiles, files, disable }: FileUploadType) => {
  const handleFileChange = (event: ChangeEvent<HTMLInputElement>, field: keyof typeof files) => {
    if (event.target.files) {
      setFiles((prev) => ({
        ...prev,
        [field]: [...prev[field], ...Array.from(event.target.files as FileList)],
      }));
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>, field: keyof typeof files) => {
    event.preventDefault();
    if (event.dataTransfer.files) {
      setFiles((prev) => ({
        ...prev,
        [field]: [...prev[field], ...Array.from(event.dataTransfer.files)],
      }));
    }
  };

  return (
    <Box width="100%">
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {[
          { label: 'Copia de Poder de los Apoderados *', name: 'LEGAL_REPRESENTATIVE_AUTHORIZATION' },
          {
            label: 'Estatuto de la Sociedad y acta que confieren autoridad a quienes otorgan poderes *',
            name: 'COMPANY_STATUTE',
          },
          { label: 'Últimos dos balances de la sociedad *', name: 'COMPANY_BALANCE_SHEET' },
        ].map((item, index) => (
          <Box key={index} width="100%">
            <Typography fontSize={11} fontWeight={500} marginBottom={1}>
              {item.label}
            </Typography>
            <Box
              sx={{
                width: '100%',
                minHeight: 100,
                border: '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2,
                textAlign: 'center',
                cursor: disable ? 'default' : 'pointer',
                '&:hover': { backgroundColor: disable ? '#FFFFFF' : '#f5f5f5' },
              }}
              onClick={() => document.getElementById(item.name)?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, item.name as keyof typeof files)}
            >
              <img src={disable ? UploadIconDisabled : UploadIcon} alt="upload" />

              <Typography fontSize={12}>
                {files[item.name as keyof typeof files].length
                  ? `${files[item.name as keyof typeof files].length} archivo(s) seleccionado(s)`
                  : 'Elegir archivos'}
              </Typography>
            </Box>
            <input
              disabled={disable}
              id={item.name}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => handleFileChange(e, item.name as keyof typeof files)}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
